/**
 * ExpandableDetailTable Component
 *
 * A lightweight nested table component for displaying detail rows in expandable
 * master-detail patterns. Uses the same styling as WarehouseTable for consistency.
 */

import React from 'react';
import { defaultFormatter } from '../utils/formatters';
import type { TableDataRow, ColumnDefinition } from '../types/table';

interface ExpandableDetailTableProps<D extends TableDataRow> {
  data: D[];
  columns: ColumnDefinition<D>[];
  emptyMessage?: string;
}

export function ExpandableDetailTable<D extends TableDataRow>({
  data,
  columns,
  emptyMessage = 'Ingen detaljer'
}: ExpandableDetailTableProps<D>) {
  // Format cell value using column configuration
  const formatCellValue = (value: any, column: ColumnDefinition<D>, row: D): string => {
    if (column.formatter) {
      return column.formatter(value, row);
    }
    return defaultFormatter(value, column.type);
  };

  if (data.length === 0) {
    return (
      <div className="px-4 py-2 text-xs text-slate-500 italic">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border-t border-slate-200">
      <table className="warehouse-table compact-mode w-full">
        <thead>
          <tr className="bg-slate-100">
            {columns.filter(col => col.visible).map(column => (
              <th
                key={column.id}
                className={`px-2 py-1 text-xs font-semibold text-slate-600 border-r border-slate-200 ${
                  column.alignment === 'center' ? 'text-center' :
                  column.alignment === 'right' ? 'text-right' : 'text-left'
                }`}
                style={{ width: column.width ? `${column.width}px` : 'auto' }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIdx) => (
            <tr key={row.id || rowIdx} className="hover:bg-slate-100 transition-colors">
              {columns.filter(col => col.visible).map(column => (
                <td
                  key={`${row.id || rowIdx}-${column.id}`}
                  className={`px-2 py-1 text-xs border-r border-slate-200 whitespace-nowrap ${
                    column.alignment === 'center' ? 'text-center' :
                    column.alignment === 'right' ? 'text-right' : 'text-left'
                  }`}
                  style={{ width: column.width ? `${column.width}px` : 'auto' }}
                  title={column.type === 'text' ? String(row[column.key] || '') : undefined}
                >
                  {column.renderer
                    ? column.renderer(row[column.key], row)
                    : formatCellValue(row[column.key], column, row)
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
